import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiExternalLink } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { useHomeBanners, useDeleteHomeBanner, useCreateHomeBanner, useUpdateHomeBanner } from '../hooks/useHomeBanners';

const HomeBanners = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  // Fetch home banners using React Query
  const {
    data: bannersResponse,
    isLoading,
    error,
    isFetching,
  } = useHomeBanners(pagination.current, pagination.pageSize);

  // Mutations
  const deleteBannerMutation = useDeleteHomeBanner();

  // Memoized banners data
  const { bannersData, totalRecords } = useMemo(() => {
    if (!bannersResponse) {
      return { bannersData: [], totalRecords: 0 };
    }

    const banners = bannersResponse.data?.data || [];
    const total = bannersResponse.data?.total || 0;

    return {
      bannersData: Array.isArray(banners) ? banners : [],
      totalRecords: total
    };
  }, [bannersResponse]);

  // Check if delete mutation is loading
  const isMutating = deleteBannerMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      deleteBannerMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'title',
      title: 'Title',
      sortable: true,
      render: (value, row) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 truncate">{value?.en || 'N/A'}</p>
        </div>
      ),
    },
    {
      key: 'text',
      title: 'Text',
      render: (value, row) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-500 truncate">{value?.en || 'N/A'}</p>
        </div>
      ),
    },
    {
      key: 'link',
      title: 'Link',
      render: (value, row) => (
        <div className="max-w-xs">
          {value ? (
            <a 
              href={value} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 inline-flex items-center"
            >
              <FiExternalLink className="h-4 w-4 mr-1" />
              <span className="truncate">View Link</span>
            </a>
          ) : (
            <span className="text-gray-400">No link</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEditBanner(row)}
            className="text-primary-500 hover:text-primary-800 p-1"
            title="Edit"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteBanner(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Handle pagination change
  const handlePageChange = (page) => {
    setPagination({ ...pagination, current: page });
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination({ current, pageSize });
  };

  const handleAddBanner = () => {
    setSelectedBanner(null);
    setShowAddModal(true);
  };

  const handleEditBanner = (banner) => {
    setSelectedBanner(banner);
    setShowEditModal(true);
  };

  const handleDeleteBanner = async (banner) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete banner "${banner.title?.en || 'N/A'}". This action cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        background: '#ffffff',
        color: '#333',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
          cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
        }
      });

      if (result.isConfirmed) {
        await deleteBannerMutation.mutateAsync(banner.id);
        Swal.fire({
          title: 'Deleted!',
          text: 'Home banner Deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedBanner(null);
  };

  const handleSuccess = () => {
    handleModalClose();
    Swal.fire({
      title: 'Success!',
      text: 'Home banner saved successfully.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
    });
  };

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="Home Banners"
          description="Manage home page banners"
          action={{
            label: 'Add Banner',
            onClick: handleAddBanner,
            icon: <FiPlus className="h-4 w-4" />,
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="bg-white rounded-lg !mb-4">
          <div className="flex items-center justify-end">
            <button
              onClick={handleAddBanner}
              className="bg-primary-500 text-white px-4 py-2 flex items-center gap-2 rounded-md hover:bg-primary-dark"
            >
              <FiPlus className="h-4 w-4" /> Add Banner
            </button>
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={bannersData}
          filterable={false}
          loading={isLoading || isFetching || isMutating}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        {/* Add Modal */}
        {showAddModal && (
          <AddHomeBanner
            onClose={handleModalClose}
            onSuccess={handleSuccess}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <EditHomeBanner
            banner={selectedBanner}
            onClose={handleModalClose}
            onSuccess={handleSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

// Add Banner Component
const AddHomeBanner = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: { ar: 'dff', en: '' },
    text: { ar: 'fds', en: '' },
    link: ''
  });

  const createBannerMutation = useCreateHomeBanner();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createBannerMutation.mutateAsync(formData);
      onSuccess();
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">Add Home Banner</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title.en}
              onChange={(e) => setFormData({
                ...formData,
                title: { ar: e.target.value, en: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text
            </label>
            <textarea
              value={formData.text.en}
              onChange={(e) => setFormData({
                ...formData,
                text: { ar: e.target.value, en: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link
            </label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({
                ...formData,
                link: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createBannerMutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
            >
              {createBannerMutation.isPending ? 'Adding...' : 'Add Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Banner Component
const EditHomeBanner = ({ banner, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: { ar: banner?.title?.ar || '', en: banner?.title?.en || '' },
    text: { ar: banner?.text?.ar || '', en: banner?.text?.en || '' },
    link: banner?.link || ''
  });

  const updateBannerMutation = useUpdateHomeBanner();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateBannerMutation.mutateAsync({ id: banner.id, data: formData });
      onSuccess();
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">Edit Home Banner</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title.en}
              onChange={(e) => setFormData({
                ...formData,
                title: { ...formData.title, en: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text
            </label>
            <textarea
              value={formData.text.en}
              onChange={(e) => setFormData({
                ...formData,
                text: { ...formData.text, en: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link
            </label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({
                ...formData,
                link: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateBannerMutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-bg-primary-500 disabled:opacity-50"
            >
              {updateBannerMutation.isPending ? 'Updating...' : 'Update Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HomeBanners; 